// Accordion.tsx
import React, { FunctionComponent, ReactNode } from 'react';
import styled from 'styled-components';
import {
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails as MuiAccordionDetails,
  AccordionProps as MuiAccordionProps,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Colors from '../global/Colors';
import Fonts from '../global/Fonts';

const StyledAccordion = styled(({ className, $colorMode, $size, $style, ...props }: { className?: string; $colorMode: 'light' | 'dark'; $size: 'small' | 'medium' | 'large'; $style: 'default' | 'ghost' } & Omit<MuiAccordionProps, 'classes'>) => (
  <MuiAccordion {...props} classes={{ root: className }} />
))<{ $colorMode: 'light' | 'dark'; $size: 'small' | 'medium' | 'large'; $style: 'default' | 'ghost' }>` 
  &.MuiAccordion-root {
    background-color: ${({ $colorMode, $style }) => ($style === 'ghost' ? 'transparent' : ($colorMode === 'light' ? Colors.white : Colors.grey9))};
    border: ${({ $colorMode, $style }) => ($style === 'ghost' ? 'none' : `1px solid ${$colorMode === 'light' ? Colors.grey1 : Colors.grey8}`)};
    border-radius: 8px;
    box-shadow: none;
    
    &:before {
      display: none;
    }

    &:hover {
      border: ${({ $colorMode, $style }) => ($style === 'ghost' ? 'none' : `1px solid ${$colorMode === 'light' ? Colors.grey2 : Colors.grey7}`)};
    }
    
    &.Mui-expanded {
      // border-color: ${({ $colorMode }) => ($colorMode === 'light' ? Colors.grey2 : Colors.grey7)};
    }
  }
`;

const StyledAccordionSummary = styled(({ className, $colorMode, $size, $style, ...props }: { className?: string; $colorMode: 'light' | 'dark'; $size: 'small' | 'medium' | 'large'; $style: 'default' | 'ghost' } & any) => (
  <MuiAccordionSummary {...props} classes={{ root: className }} />
))<{ $colorMode: 'light' | 'dark'; $size: 'small' | 'medium' | 'large'; $style: 'default' | 'ghost' }>` 
  &.MuiAccordionSummary-root {
    padding: ${({ $size, $style }) => {
      if ($style === 'ghost') {
        return '0';
        // switch ($size) {
        //   case 'small': return '6px 0px';
        //   case 'medium': return '9px 0px';
        //   case 'large': return '12px 0px';
        //   default: return '9px 0px';
        // }
      }
      switch ($size) {
        case 'small': return '8px 12px';
        case 'medium': return '12px 16px';
        case 'large': return '16px 20px';
        default: return '12px 16px';
      }
    }};
    min-height: ${({ $size }) => {
      switch ($size) {
        case 'small': return '36px';
        case 'medium': return '48px';
        case 'large': return '56px';
        default: return '48px';
      }
    }};
    
    &.Mui-expanded {
      min-height: ${({ $size }) => {
        switch ($size) {
          case 'small': return '36px';
          case 'medium': return '48px';
          case 'large': return '56px';
          default: return '48px';
        }
      }};
      // border-bottom: 1px solid ${({ $colorMode }) => ($colorMode === 'light' ? Colors.background : Colors.grey8)};
    }
  }
  
  & .MuiAccordionSummary-content {
    margin: 0;
    
    &.Mui-expanded {
      margin: 0;
    }
  }
  
  & .MuiAccordionSummary-expandIconWrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    color: ${({ $colorMode }) => ($colorMode === 'light' ? Colors.grey4 : Colors.grey6)};
    // color: ${Colors.grey5};
    
    & svg {
      width: ${({ $size }) => {
        switch ($size) {
          case 'small': return '20px';
          case 'medium': return '22px';
          case 'large': return '24px';
          default: return '22px';
        }
      }};
      height: ${({ $size }) => {
        switch ($size) {
          case 'small': return '20px';
          case 'medium': return '22px';
          case 'large': return '24px';
          default: return '22px';
        }
      }};
    }
    
    &.Mui-expanded {
      transform: rotate(180deg);
    }
  }
`;

const StyledAccordionDetails = styled(({ className, $colorMode, $size, $style, ...props }: { className?: string; $colorMode: 'light' | 'dark'; $size: 'small' | 'medium' | 'large'; $style: 'default' | 'ghost' } & any) => (
  <MuiAccordionDetails {...props} classes={{ root: className }} />
))<{ $colorMode: 'light' | 'dark'; $size: 'small' | 'medium' | 'large'; $style: 'default' | 'ghost' }>` 
  &.MuiAccordionDetails-root {
    padding: ${({ $size, $style }) => {
      if ($style === 'ghost') {
        switch ($size) {
          case 'small': return '2px 0px 10px 0px';
          case 'medium': return '3px 0px 14px 0px';
          case 'large': return '4px 0px 18px 0px';
          default: return '3px 0px 9px 0px';
        }
      }
      switch ($size) {
        case 'small': return '2px 12px 10px 12px';
        case 'medium': return '3px 16px 14px 16px';
        case 'large': return '4px 20px 18px 20px';
        default: return '3px 16px 9px 16px';
      }
    }};
    border-radius: 8px;
    background-color: ${({ $colorMode, $style }) => ($style === 'ghost' ? 'transparent' : ($colorMode === 'light' ? Colors.white : Colors.grey9))};
  }
`;

const SummaryContent = styled.div<{ $colorMode: 'light' | 'dark'; $size: 'small' | 'medium' | 'large'; $style: 'default' | 'ghost' }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $size }) => {
    switch ($size) {
      case 'small': return '1px';
      case 'medium': return '2px';
      case 'large': return '3px';
      default: return '2px';
    }
  }};
  width: 100%;
`;

const MainText = styled.div<{ $colorMode: 'light' | 'dark'; $size: 'small' | 'medium' | 'large'; $style: 'default' | 'ghost' }>`
  font-family: ${Fonts.lexendMedium.fontFamily};
  font-weight: ${Fonts.lexendMedium.fontWeight};
  font-size: ${({ $size }) => {
    switch ($size) {
      case 'small': return '14px';
      case 'medium': return '16px';
      case 'large': return '18px';
      default: return '16px';
    }
  }};
  line-height: 1.2em;
  color: ${({ $colorMode }) => ($colorMode === 'light' ? Colors.grey8 : Colors.grey2)} !important;
`;

const SubText = styled.div<{ $colorMode: 'light' | 'dark'; $size: 'small' | 'medium' | 'large'; $style: 'default' | 'ghost' }>`
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: ${({ $size }) => {
    switch ($size) {
      case 'small': return '13px';
      case 'medium': return '14px';
      case 'large': return '16px';
      default: return '14px';
    }
  }};
  line-height: 1.2em;
  color: ${({ $colorMode }) => ($colorMode === 'light' ? Colors.grey7 : Colors.grey3)} !important;
`;

const DetailContent = styled.div<{ $colorMode: 'light' | 'dark'; $size: 'small' | 'medium' | 'large'; $style: 'default' | 'ghost' }>`
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: ${({ $size }) => {
    switch ($size) {
      case 'small': return '13px';
      case 'medium': return '14px';
      case 'large': return '16px';
      default: return '14px';
    }
  }};
  line-height: 1.4em;
  color: ${({ $colorMode }) => ($colorMode === 'light' ? Colors.grey7 : Colors.grey3)} !important;
  white-space: pre-wrap; // preserve new lines
`;

interface AccordionProps {
  title: string;
  subtitle?: string;
  content: string | string[];
  colorMode?: 'light' | 'dark';
  size?: 'small' | 'medium' | 'large';
  style?: 'default' | 'ghost';
  defaultExpanded?: boolean;
  expanded?: boolean;
  onChange?: (event: React.SyntheticEvent, isExpanded: boolean) => void;
  disabled?: boolean;
}

const Accordion: FunctionComponent<AccordionProps> = ({
  title,
  subtitle,
  content,
  colorMode = 'dark',
  size = 'medium',
  style = 'default',
  defaultExpanded = false,
  expanded,
  onChange,
  disabled = false,
}) => {
  const summaryContent = (
    <SummaryContent $colorMode={colorMode} $size={size} $style={style}>
      <MainText $colorMode={colorMode} $size={size} $style={style}>{title}</MainText>
      {subtitle && <SubText $colorMode={colorMode} $size={size} $style={style}>{subtitle}</SubText>}
    </SummaryContent>
  );

  // Function to render content with newlines
  const renderContentWithNewlines = (text: string | string[]) => {
    if (Array.isArray(text)) {
      return text.map((item, index) => (
        <React.Fragment key={index}>
          {item.split('\n').map((line, lineIndex) => (
            <React.Fragment key={lineIndex}>
              {line}
              {lineIndex < line.length - 1 && <br />}
            </React.Fragment>
          ))}
          {index < text.length - 1 && <br />}
        </React.Fragment>
      ));
    }
    return text.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < text.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };


  return (
    <StyledAccordion
      $colorMode={colorMode}
      $size={size}
      $style={style}
      defaultExpanded={defaultExpanded}
      expanded={expanded}
      onChange={onChange}
      disabled={disabled}
    >
      <StyledAccordionSummary
        $colorMode={colorMode}
        $size={size}
        $style={style}
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel-content"
        id="panel-header"
      >
        {summaryContent}
      </StyledAccordionSummary>
      <StyledAccordionDetails $colorMode={colorMode} $size={size} $style={style}>
        <DetailContent $colorMode={colorMode} $size={size} $style={style}>
          {renderContentWithNewlines(content)}
        </DetailContent>
      </StyledAccordionDetails>
    </StyledAccordion>
  );
};

export default Accordion;